import React, { useContext, useEffect, useRef, useState } from "react";
import {
  AreaOfInterestType,
  PostWindowEventName,
  ProcedureType,
  TFormName,
  TScreen,
  UploadTypeType,
  ViewType,
} from "api/type";

// Import Components
import { NormalText } from "components/index";
import ScreenScan from "./ScreenScan";
import ScreenScanInstruction from "./ScreenScanInstruction";
import ScreenSelectModel from "./ScreenSelectModel";
import ScreenSelectProcedure from "./ScreenSelectProcedure";
import ScreenSelectAreasOfInterest from "./ScreenSelectAreasOfInterest";
import ScreenSelectUploadMethod from "./ScreenSelectUploadMethod";
import ScreenUploadPhotoInstruction from "./ScreenUploadPhotoInstruction";
import ScreenVisualize from "./ScreenVisualize";
import ScreenUploadPhoto from "./ScreenUploadPhoto";

// Import Contexts
import ScreenContext from "contexts/screen";
import { ClinicContext, SessionContext } from "contexts";

// Import APIs
import {
  postPatientsImagesBatch,
  TPostPatientsImagesBatchResponse,
  TPostPatientsImagesBatchRequest,
  TPostTransformsResponse,
  TPostTransformsRequest,
  postTransforms,
  TPostTransformsBatchResponse,
  TPostTransformsBatchRequest,
  postTransformsBatch,
} from "api/clinicos";

// Import MediaPipe
import { PoseLandmarker } from "@mediapipe/tasks-vision";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import {
  isFaceProcedure,
  isTorsoProcedure,
} from "utils/isFaceBodyOrTorsoProcedure";
import { mixpanelTrack } from "utils/mixpanel";
import {
  callFormSimulationTracking,
  callSiteVisitTracking,
  callReferralTracking,
  callFormVisitTracking,
  callCTATracking,
} from "utils/callClinicosTracking";
import { useContextProcedure, usePoseLandmarker } from "utils/customHook";

type RootOnboardingTier1Props = {};

const RootOnboardingTier1: React.FC<RootOnboardingTier1Props> = () => {
  // Context and State Declarations
  const session = useContext(SessionContext);
  const clinic = useContext(ClinicContext);
  const [screen, setScreen] = useContext(ScreenContext);

  // Custom Hooks
  const poseLandmarker: PoseLandmarker | null | "ERROR" = usePoseLandmarker();

  const contextProcedure = useContextProcedure();
  const contextProcedureRef = useRef<ProcedureType | null>(null);

  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [selectedAreasOfInterest, setSelectedAreasOfInterest] = useState<
    AreaOfInterestType[]
  >([]);
  const [selectedProcedure, setSelectedProcedure] =
    useState<ProcedureType | null>(null);
  const [selectedImageUrlOrBlob, setSelectedUrlOrBlob] = useState<
    string | Blob | null
  >(null);

  const [postTransformsResponse, setPostTransformsResponse] =
    useState<TPostTransformsResponse | null>(null);
  const [postTransformsBatchResponse, setPostTransformsBatchResponse] =
    useState<TPostTransformsBatchResponse | null>(null);
  const [currentBatchId, setCurrentBatchId] = useState<string>("");
  const [uploadType, setUploadType] = useState<UploadTypeType>(
    UploadTypeType.MODEL,
  );

  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    contextProcedureRef.current = contextProcedure;
  }, [contextProcedure]);

  useEffect(() => {
    window.addEventListener("message", (ev) => {
      const payLoad = ev.data;
      if (payLoad.message === PostWindowEventName.FROM_OPENED) {
        const hasCalledFormVisitMixpanel = sessionStorage.getItem(
          "hasCalledFormVisitMixpanel",
        );

        if (!hasCalledFormVisitMixpanel) {
          mixpanelTrack("Form Started", [
            {
              propertyName: "Clinic Name",
              propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
            },
          ]);
          sessionStorage.setItem("hasCalledFormVisitMixpanel", "true");
        }

        callFormVisitTracking(session, TFormName.ONBOARDING_TIER1);
        callCTATracking(session, contextProcedureRef.current);
      }
    });
    // NOTE: It's ok to not include clinic in the dependency array
    // eslint-disable-next-line
  }, []);

  // Effect: Fetch After Image URLs
  useEffect(() => {
    const fetchAfterImageUrls = async () => {
      try {
        if (!session || !selectedImageUrlOrBlob || !selectedProcedure) return;

        callFormSimulationTracking(session, TFormName.ONBOARDING_TIER1);

        let beforeImageUrl = "";
        let viewType: ViewType | null;

        // Determine the view type based on the selectedProcedure
        switch (selectedProcedure) {
          case ProcedureType.BREAST_AUGMENTATION:
          case ProcedureType.BREAST_REDUCTION:
          case ProcedureType.BREAST_LIFT:
          case ProcedureType.BREAST_LIFT_AND_AUGMENTATION:
            viewType = ViewType.TORSO_FRONT;
            break;
          case ProcedureType.TUMMY_TUCK:
            viewType = ViewType.BODY_FRONT;
            break;
          case ProcedureType.BBL:
            viewType = ViewType.BODY_BACK;
            break;
          default:
            if (isTorsoProcedure(selectedProcedure)) {
              viewType = ViewType.TORSO_FRONT;
            } else if (isFaceProcedure(selectedProcedure)) {
              viewType = ViewType.HEAD_FRONT;
            } else viewType = ViewType.BODY_FRONT;
        }

        // Handle image upload and retrieval
        if (selectedImageUrlOrBlob instanceof Blob) {
          const body: TPostPatientsImagesBatchRequest = {
            images: [
              {
                view_type: viewType,
                procedure_types: [selectedProcedure],
              },
            ],
          };
          const result: TPostPatientsImagesBatchResponse =
            await postPatientsImagesBatch(session, body, [
              selectedImageUrlOrBlob,
            ]);
          if (result.presigned_urls.length !== 1) {
            throw new Error("Invalid number of presigned urls");
          }

          beforeImageUrl = result.presigned_urls[0].get_url;
          setCurrentBatchId(result.batch_id);

          // Dont do transformation if the procedure is not supported by AI
          if (!isAISupportedProcedure(selectedProcedure)) return;

          const secondBody: TPostTransformsBatchRequest = {
            batch_id: result.batch_id,
            request: [
              {
                image_url: beforeImageUrl,
                procedure_types: [selectedProcedure],
                procedure_view: viewType,
              },
            ],
          };
          const secondResult: TPostTransformsBatchResponse =
            await postTransformsBatch(session, secondBody);
          setPostTransformsBatchResponse(secondResult);
        } else if (typeof selectedImageUrlOrBlob === "string") {
          beforeImageUrl = selectedImageUrlOrBlob;
          const body: TPostTransformsRequest = {
            image_url: beforeImageUrl,
            procedure_types: [selectedProcedure],
            procedure_view: viewType,
          };
          const result: TPostTransformsResponse = await postTransforms(
            session,
            body,
          );
          setPostTransformsResponse(result);
        }
        mixpanelTrack("Simulation Complete", [
          {
            propertyName: "Clinic Name",
            propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
          },
          {
            propertyName: "Procedure",
            propertyValue: selectedProcedure ?? "failed to get procedure",
          },
          {
            propertyName: "Simulation Type",
            propertyValue: "Body Scan",
          },
        ]);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes("401")) {
            setErrorMessage(
              "Session has expired. Page will refresh in 3 seconds.",
            );
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          } else {
            setErrorMessage(error.message);
          }
        } else {
          setErrorMessage("Error fetching after image urls");
        }
      }
    };
    fetchAfterImageUrls();

    // NOTE: It is safe to not include selectedImageUrlOrBlob in the dependency array
    // Including it will cause a bug where the user goes back to the SelectModel screen,
    // selects a procedure, and then the Visualize screen shows 2 simulations.
    // eslint-disable-next-line
  }, [selectedImageUrlOrBlob, session]);

  // Effect: Set starting screen
  useEffect(() => {
    setScreen(TScreen.SELECT_AREA_OF_INTEREST_SCREEN);
  }, [setScreen]);

  // Effect: Set dynamic starting screen
  useEffect(() => {
    if (!contextProcedure) {
      setSelectedAreasOfInterest([]);
      setSelectedProcedure(null);
      setScreen(TScreen.SELECT_AREA_OF_INTEREST_SCREEN);
    }
    if (contextProcedure === ProcedureType.BREAST_AUGMENTATION) {
      setSelectedAreasOfInterest([AreaOfInterestType.BREAST]);
      setSelectedProcedure(ProcedureType.BREAST_AUGMENTATION);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    } else if (contextProcedure === ProcedureType.BREAST_LIFT) {
      setSelectedAreasOfInterest([AreaOfInterestType.BREAST]);
      setSelectedProcedure(ProcedureType.BREAST_LIFT);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    } else if (contextProcedure === ProcedureType.BREAST_REDUCTION) {
      setSelectedAreasOfInterest([AreaOfInterestType.BREAST]);
      setSelectedProcedure(ProcedureType.BREAST_REDUCTION);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    } else if (
      contextProcedure === ProcedureType.BREAST_LIFT_AND_AUGMENTATION
    ) {
      setSelectedAreasOfInterest([AreaOfInterestType.BREAST]);
      setSelectedProcedure(ProcedureType.BREAST_LIFT_AND_AUGMENTATION);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    } else if (contextProcedure === ProcedureType.TUMMY_TUCK) {
      setSelectedAreasOfInterest([AreaOfInterestType.TORSO]);
      setSelectedProcedure(ProcedureType.TUMMY_TUCK);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    } else if (contextProcedure === ProcedureType.BBL) {
      setSelectedAreasOfInterest([AreaOfInterestType.BUTT]);
      setSelectedProcedure(ProcedureType.BBL);
      setScreen(TScreen.SELECT_MODEL_SCREEN);
    }
  }, [setScreen, contextProcedure]);

  // Effect: Call Referral and Site Visit Tracking
  useEffect(() => {
    if (!session) return;
    callReferralTracking(session, TFormName.ONBOARDING_TIER1);
    callSiteVisitTracking(session, TFormName.ONBOARDING_TIER1);
  }, [session]);

  // Helper Functions
  const handleSaveImageUrlOrBlob = async (
    imageUrlOrBlob: string | Blob | null,
  ) => {
    setPostTransformsResponse(null);
    setPostTransformsBatchResponse(null);
    setSelectedUrlOrBlob(imageUrlOrBlob);
  };

  const handleSaveSelectedProcedure = (selectedProcedure: ProcedureType) => {
    setSelectedProcedure(selectedProcedure);
  };

  const handleSaveSelectedAreaOfInterest = (
    areasOfInterest: AreaOfInterestType[],
  ) => {
    setSelectedAreasOfInterest(areasOfInterest);
  };

  const handleSetIsFormSubmittedTrue = () => {
    setIsFormSubmitted(true);
  };

  const handleSetUploadType = (uploadType: UploadTypeType) => {
    setUploadType(uploadType);
  };

  // Render Screen based on the current context
  const renderScreen = () => {
    switch (screen) {
      case TScreen.SELECT_AREA_OF_INTEREST_SCREEN:
        return (
          <ScreenSelectAreasOfInterest
            selectedAreaOfInterest={selectedAreasOfInterest}
            handleSaveSelectedAreaOfInterest={handleSaveSelectedAreaOfInterest}
          />
        );
      case TScreen.SELECT_PROCEDURE_SCREEN:
        return (
          <ScreenSelectProcedure
            selectedAreasOfInterest={selectedAreasOfInterest}
            selectedProcedure={selectedProcedure}
            handleSaveSelectedProcedure={handleSaveSelectedProcedure}
          />
        );
      case TScreen.SELECT_MODEL_SCREEN:
        return (
          <ScreenSelectModel
            selectedProcedure={selectedProcedure}
            handleSaveImageUrlOrBlob={handleSaveImageUrlOrBlob}
            handleSetUploadType={handleSetUploadType}
          />
        );
      case TScreen.SELECT_UPLOAD_TYPE_SCREEN:
        return (
          <ScreenSelectUploadMethod handleSetUploadType={handleSetUploadType} />
        );
      case TScreen.PHOTO_UPLOAD_INSTRUCTIONS_SCREEN:
        return <ScreenUploadPhotoInstruction />;
      case TScreen.PHOTO_UPLOAD_SCREEN:
        return (
          <ScreenUploadPhoto
            selectedProcedure={selectedProcedure}
            handleSaveImageUrlOrBlob={handleSaveImageUrlOrBlob}
          />
        );
      case TScreen.BODY_SCAN_INSTRUCTIONS_SCREEN:
        return <ScreenScanInstruction />;
      case TScreen.BODY_SCAN_SCREEN:
        return (
          <ScreenScan
            poseLandmarker={poseLandmarker}
            selectedProcedure={selectedProcedure}
            handleSaveImageUrlOrBlob={handleSaveImageUrlOrBlob}
          />
        );
      case TScreen.SIMULATION_VIEW_SCREEN:
        return (
          <ScreenVisualize
            batchId={currentBatchId}
            isFormSubmitted={isFormSubmitted}
            handleSetIsFormSubmittedTrue={handleSetIsFormSubmittedTrue}
            handleSaveImageUrlOrBlob={handleSaveImageUrlOrBlob}
            selectedProcedure={selectedProcedure}
            postTransformsResponse={postTransformsResponse}
            postTransformsBatchResponse={postTransformsBatchResponse}
            uploadType={uploadType}
          />
        );
      default:
        return null;
    }
  };

  if (errorMessage) return <NormalText textContent={errorMessage} />;

  return <div id="onboarding-advertise-container">{renderScreen()}</div>;
};

export default RootOnboardingTier1;
